import React, { useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Popconfirm,
  Space, Table, Typography, message,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  EquipmentType,
  getEquipmentTypes,
  createEquipmentType,
  updateEquipmentType,
  deleteEquipmentType,
} from '../api/equipment-types';

const { Title } = Typography;

const EquipmentTypesPage: React.FC = () => {
  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EquipmentType | null>(null);
  const [form] = Form.useForm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { setTypes(await getEquipmentTypes()); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: EquipmentType) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name });
    setModalOpen(true);
  };

  const handleSubmit = async ({ name }: { name: string }) => {
    try {
      if (editing) {
        const updated = await updateEquipmentType(editing.id, name);
        setTypes(prev => prev.map(t => t.id === updated.id ? updated : t));
        message.success('Обновлено');
      } else {
        const created = await createEquipmentType(name);
        setTypes(prev => [...prev, created]);
        message.success('Создано');
      }
      setModalOpen(false);
    } catch {
      message.error('Ошибка при сохранении');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteEquipmentType(id);
      setTypes(prev => prev.filter(t => t.id !== id));
      message.success('Удалено');
    } catch {
      message.error('Ошибка при удалении');
    }
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: any, record: EquipmentType) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Удалить тип оборудования?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Типы оборудования</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={types}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
      />
      <Modal
        title={editing ? 'Редактировать тип' : 'Новый тип'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EquipmentTypesPage;
